import { Link, useOutletContext } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, Award, BookOpen, Heart, Quote, Scale, HandshakeIcon, Users, ShieldCheck, CalendarDays, Sparkles } from "lucide-react";
import Marquee from "@/components/Marquee";
import SupportDialog from "@/components/SupportDialog";
import { api } from "@/lib/api";

const HERO_BG = "https://images.unsplash.com/photo-1764083281098-7cd2899a3d43?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const PORTRAIT = "https://customer-assets.emergentagent.com/job_doctor-leader-delhi/artifacts/l564kqho_WhatsApp%20Image%202026-05-04%20at%2011.48.18%20PM.jpeg";
const CROWD = "https://customer-assets.emergentagent.com/job_doctor-leader-delhi/artifacts/1ru24cyd_WhatsApp%20Image%202026-04-30%20at%208.37.29%20PM.jpeg";
const SANKALP_LOGO = "https://customer-assets.emergentagent.com/job_doctor-leader-delhi/artifacts/r36y6jy5_WhatsApp%20Image%202026-05-05%20at%2012.52.39%20AM.jpeg";
const VOTER_CHART = "https://customer-assets.emergentagent.com/job_doctor-leader-delhi/artifacts/kn85o6u3_WhatsApp%20Image%202026-05-09%20at%209.33.12%20AM.jpeg";
const YOUTUBE_ID = "JsN2JoaCUYk";

const PILLARS = [
  { icon: Scale, t: "Ethical", d: "Uncompromising ethics in practice and council governance." },
  { icon: HandshakeIcon, t: "Accessible", d: "Open-door council. Reachable, responsive, listening." },
  { icon: ShieldCheck, t: "Committed", d: "Decade-long record of doctor welfare and student leadership." },
  { icon: Sparkles, t: "Transparent", d: "Public dashboards, RTI-first culture, accountable decisions." },
];

const INITIATIVES = [
  { y: "2026", t: "F.A.M.E", d: "Family Adoption & Medical Education" },
  { y: "2025", t: "S.A.N.K.A.L.P", d: "Sewa-oriented Alliance for Nation-building, Knowledge, Accountability, Leadership & Policy" },
  { y: "2024", t: "Project Gyan Sindhu", d: "Education of children in Ladakh" },
  { y: "2024", t: "WeVaku Foundation", d: "Social work for underserved communities" },
  { y: "2016", t: "Organum Donum", d: "Organ & body donation awareness society" },
];

export default function Home() {
  const ctx = useOutletContext();
  const settings = ctx?.settings;
  const [endorsements, setEndorsements] = useState([]);

  useEffect(() => {
    api.get("/endorsements").then((r) => setEndorsements(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const heroTitle = settings?.hero_title || "Vote Dr. Abhishek Garg";
  const heroSlogan = settings?.hero_slogan || "Strong Representation. Transparent Governance. Better Future for Doctors.";
  const heroSubtitle = settings?.hero_subtitle || "Young. Ethical. Accessible. Committed. | DMC Elections — 31st May 2026";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden">
        <img src={HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="container-x relative z-10 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8 text-white">
            <div className="flex items-center gap-3 mb-5">
              <img src={SANKALP_LOGO} alt="Sankalp" className="w-12 h-12 object-contain bg-white rounded-full p-1" />
              <div className="label-eyebrow text-[hsl(var(--accent))]">Team Sankalp · DMC Election</div>
            </div>
            <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9] tracking-tight">
              Vote
              <span className="block text-[hsl(var(--accent))]">Dr. Abhishek Garg</span>
              <span className="block text-3xl sm:text-4xl lg:text-5xl mt-3 font-display text-white/90">For DMC Executive Member</span>
            </h1>
            <p className="mt-6 text-xl sm:text-2xl font-light text-white/90 max-w-2xl">{heroSlogan}</p>
            <p className="mt-3 text-base text-white/75 max-w-xl">{heroSubtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a href="#support" data-testid="hero-support-btn" className="btn-primary text-base px-8 py-4">
                Support Now <ArrowRight size={16} />
              </a>
              <Link to="/about" data-testid="hero-about-btn"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-white hover:text-[hsl(var(--primary))] transition-all">
                Know Your Candidate
              </Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-4">
            <div className="relative">
              <div className="aspect-[3/4] bg-white border-8 border-white shadow-2xl overflow-hidden">
                <img src={PORTRAIT} alt="Dr. Abhishek Garg" className="w-full h-full object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 bg-[hsl(var(--accent))] text-white p-4">
                <div className="text-[10px] uppercase tracking-[0.25em]">Vote on</div>
                <div className="font-display text-2xl">31 May 2026</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee items={["SEWA", "SAMARPAN", "SANKALP", "SAFALTA", "ETHICAL", "ACCESSIBLE", "COMMITTED", "TRANSPARENT"]} />

      {/* WATCH: YouTube + Voter Turnout poster */}
      <section className="section-y" data-testid="watch-section">
        <div className="container-x">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="label-eyebrow mb-3">Hear from the candidate</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[hsl(var(--primary))]">
                Dr. Garg on the real DMC problems.
              </h2>
              <p className="mt-3 text-[hsl(var(--fg-muted))]">
                A direct, unfiltered look at what's broken — and what we will fix together.
              </p>
              <div className="mt-6 relative aspect-video w-full bg-[hsl(var(--primary))] overflow-hidden border border-[hsl(var(--border-c))]">
                <iframe
                  data-testid="youtube-embed"
                  src={`https://www.youtube.com/embed/${YOUTUBE_ID}?rel=0&modestbranding=1`}
                  title="Dr. Abhishek Garg on DMC problems"
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              </div>
              <a
                href={`https://www.youtube.com/watch?v=${YOUTUBE_ID}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-widest font-semibold text-[hsl(var(--accent))] mt-4 hover:underline"
                data-testid="youtube-link"
              >
                Watch on YouTube <ArrowRight size={14} />
              </a>
            </div>
            <div className="lg:col-span-5">
              <div className="label-eyebrow mb-3">Why your vote matters</div>
              <h3 className="font-display text-3xl text-[hsl(var(--primary))]">Every Doctor Counts.</h3>
              <p className="text-sm text-[hsl(var(--fg-muted))] mt-2">
                DMC voter turnout has been worryingly low. In 2014: <strong>7.6%</strong>. In 2020: <strong>9.6%</strong>.
                Let 2025–26 be the year doctors show up — for representation, voice, accountability, and unity.
              </p>
              <div className="mt-5 border border-[hsl(var(--border-c))] overflow-hidden">
                <img
                  src={VOTER_CHART}
                  alt="Voter turnout in DMC Elections"
                  loading="lazy"
                  className="w-full h-auto block"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ELECTION DATES STRIP */}
      <section className="bg-[hsl(var(--bg-muted))] border-b border-[hsl(var(--border-c))]">
        <div className="container-x py-8 grid sm:grid-cols-2 gap-6">
          <div className="flex items-start gap-4">
            <CalendarDays className="text-[hsl(var(--accent))] shrink-0 mt-1" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--fg-muted))]">DMA-DMC Elections</div>
              <div className="font-display text-2xl text-[hsl(var(--primary))]">17th May 2026 (Sunday)</div>
              <div className="text-sm text-[hsl(var(--fg-muted))]">Voters: DMA Members · Venue: DMA House</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <CalendarDays className="text-[hsl(var(--accent))] shrink-0 mt-1" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--fg-muted))]">Delhi Medical Council Elections</div>
              <div className="font-display text-2xl text-[hsl(var(--primary))]">31st May 2026 (Sunday)</div>
              <div className="text-sm text-[hsl(var(--fg-muted))]">Voters: All Registered Medical Practitioners · Venue: DMC Office</div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="section-y">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-6">
            <div className="md:col-span-5">
              <div className="label-eyebrow mb-3">Why Dr. Garg</div>
              <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[hsl(var(--primary))]">
                Young. Ethical. Accessible. Committed.
              </h2>
            </div>
            <div className="md:col-span-7 text-[hsl(var(--fg-muted))] text-lg leading-relaxed">
              MBBS (UCMS) · MD (LHMC). Senior Resident at UCMS. Primary Care Physician & Public Health Specialist.
              Founder of <span className="text-[hsl(var(--primary))] font-semibold">Team S.A.N.K.A.L.P</span> and a proven leader across NMO,
              IMA East Delhi, IAPSM, and UCMS Alumni Association — with <span className="font-semibold">17+ academic contributions</span> and <span className="font-semibold">26 awards</span>.
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mt-14">
            {PILLARS.map((p, i) => (
              <div key={i} className="card-flat" data-testid={`pillar-${p.t.toLowerCase()}`}>
                <p.icon className="text-[hsl(var(--accent))]" size={32} />
                <h3 className="font-display text-2xl mt-4 text-[hsl(var(--primary))]">{p.t}</h3>
                <p className="mt-2 text-[hsl(var(--fg-muted))] text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER INITIATIVES */}
      <section className="bg-[hsl(var(--primary))] text-white section-y">
        <div className="container-x">
          <div className="label-eyebrow text-[hsl(var(--accent))] mb-3">Founder & Initiatives</div>
          <h2 className="font-display text-4xl sm:text-5xl">A decade of building, beyond the clinic.</h2>
          <div className="mt-12 grid md:grid-cols-5 gap-6">
            {INITIATIVES.map((it, i) => (
              <div key={i} className="border border-white/20 p-6 hover:border-[hsl(var(--accent))] transition-colors" data-testid={`initiative-${i}`}>
                <div className="font-display text-3xl text-[hsl(var(--accent))]">{it.y}</div>
                <div className="font-display text-xl mt-2">{it.t}</div>
                <div className="text-xs opacity-80 mt-1 leading-relaxed">{it.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UPDATES + SUPPORT FORM */}
      <section className="bg-[hsl(var(--bg-muted))] section-y" id="support">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="label-eyebrow mb-3">A snapshot</div>
            <h2 className="font-display text-4xl sm:text-5xl text-[hsl(var(--primary))]">By the numbers</h2>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4">
              {[
                { k: "17+", l: "Publications" },
                { k: "26", l: "Awards" },
                { k: "5+", l: "Founder Roles" },
                { k: "10+", l: "Leadership Posts" },
              ].map((s) => (
                <div key={s.l} className="bg-white border border-[hsl(var(--border-c))] py-7 text-center">
                  <div className="font-display text-4xl text-[hsl(var(--accent))]">{s.k}</div>
                  <div className="text-[10px] uppercase tracking-widest mt-1 text-[hsl(var(--fg-muted))]">{s.l}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              <Link to="/dmc-election" className="bg-[hsl(var(--primary))] text-white p-6 hover:bg-[hsl(var(--primary-hover))] transition-all" data-testid="quick-dmc">
                <BookOpen className="mb-3" />
                <div className="font-display text-xl">DMC Election</div>
                <div className="text-xs opacity-80 mt-1">Manifesto, Act, Vision</div>
              </Link>
              <Link to="/endorsements" className="bg-white border border-[hsl(var(--border-c))] p-6 hover:border-[hsl(var(--accent))] transition-all" data-testid="quick-endorsements">
                <Quote className="mb-3 text-[hsl(var(--accent))]" />
                <div className="font-display text-xl text-[hsl(var(--primary))]">Endorsements</div>
                <div className="text-xs text-[hsl(var(--fg-muted))] mt-1">Voices of support</div>
              </Link>
              <Link to="/awards" className="bg-white border border-[hsl(var(--border-c))] p-6 hover:border-[hsl(var(--accent))] transition-all" data-testid="quick-awards">
                <Award className="mb-3 text-[hsl(var(--accent))]" />
                <div className="font-display text-xl text-[hsl(var(--primary))]">26 Awards</div>
                <div className="text-xs text-[hsl(var(--fg-muted))] mt-1">Local · State · National</div>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border-2 border-[hsl(var(--primary))] p-8">
              <div className="label-eyebrow mb-2">Pledge support</div>
              <h3 className="font-display text-3xl text-[hsl(var(--primary))] mb-1">Stand With Dr. Garg</h3>
              <p className="text-sm text-[hsl(var(--fg-muted))] mb-6">
                Every supporter strengthens the movement. Share your voice.
              </p>
              <SupportDialog embedded />
            </div>
          </div>
        </div>
      </section>

      {/* ENDORSEMENTS */}
      <section className="section-y">
        <div className="container-x">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <div>
              <div className="label-eyebrow mb-3">Voices of support</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[hsl(var(--primary))]">Endorsed by the community</h2>
            </div>
            <Link to="/endorsements" className="btn-outline" data-testid="see-all-endorsements">See all <ArrowRight size={16} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {endorsements.map((e) => (
              <div key={e.id} className="card-flat" data-testid={`endorsement-${e.id}`}>
                <Quote className="text-[hsl(var(--accent))]" />
                <p className="mt-4 text-[hsl(var(--fg))] leading-relaxed">"{e.message}"</p>
                <div className="mt-6 pt-4 border-t border-[hsl(var(--border-c))]">
                  <div className="font-display text-xl text-[hsl(var(--primary))]">{e.name}</div>
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--fg-muted))]">{e.designation}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="relative bg-[hsl(var(--primary))] text-white">
        <img src={CROWD} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="container-x relative z-10 py-20 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl sm:text-5xl">
            A Proven Leader. A Trusted Voice. <span className="text-[hsl(var(--accent))]">A Doctor Who Delivers.</span>
          </h2>
          <div className="flex flex-wrap gap-4 md:justify-end">
            <a href="#support" className="btn-primary text-base px-8 py-4" data-testid="cta-support">
              <Heart size={16} /> Support Now
            </a>
            <Link to="/connect" className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 font-semibold uppercase tracking-wider text-sm hover:bg-white hover:text-[hsl(var(--primary))]" data-testid="cta-connect">
              Connect With Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
